import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * Aviso exibido quando uma frase não pôde ser traduzida (sem internet ou Google falhou)
 * e o tradutor local ainda não foi baixado. "Baixar agora" leva a /settings/translation.
 */
export const OfflineTranslatorPromptModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="offline-translator-prompt"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10060] flex items-center justify-center p-5 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-sm rounded-[28px] bg-surface text-ink p-7 text-center border border-black/[0.06] dark:border-white/[0.06]"
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-tint-sky flex items-center justify-center">
              <Languages size={24} strokeWidth={1.6} />
            </div>
            <h3 className="font-display text-2xl font-light tracking-[-0.03em] leading-tight">
              {t('offlineTr.modalTitle', 'Tradução indisponível')}
            </h3>
            <p className="mt-3 text-sm text-muted leading-relaxed">
              {t('offlineTr.modalText')}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <button onClick={onClose} className="h-11 rounded-full text-sm font-medium text-muted hover:text-ink border border-black/[0.08] dark:border-white/[0.08] cursor-pointer">
                {t('offlineTr.cancel', 'Cancelar')}
              </button>
              <button
                onClick={() => { onClose(); navigate('/settings/translation'); }}
                className="h-11 rounded-full text-sm font-medium bg-primary text-slate-900 hover:opacity-90 cursor-pointer"
              >
                {t('offlineTr.downloadNow', 'Baixar agora')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default OfflineTranslatorPromptModal;
